import "./ChatCategories.css";
import { useHeaderCategories } from "../../hooks/useHeaderCategories";
import { FiSidebar } from "react-icons/fi";
import { useEffect, useRef, useState } from "react";
import useFeatures from "../../hooks/useFeatures";
import { useNavigate } from "react-router-dom";
import { CiCirclePlus } from "react-icons/ci";
import { setMode } from "../ChatApp/chatSlice";
import { useHeaderChatActions } from "../../hooks/useHeaderChatActions";
import { useDispatch } from "react-redux";



const easeInOut = (t) =>
  t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

const ChatCategories = ({ setChatType, handleClickToDialogs }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canUseChat, hasSubscription, planType, loaded, loadFeatures, availableChatTypes } = useFeatures();
  const { handleStartNewDialog, handleStartProcessingMessageToLawyer } = useHeaderChatActions();
  const [toast, setToast] = useState(null);

  const {
    selectedSubOption,
    categoryOptions,
    handleSubOptionChange,
  } = useHeaderCategories(setChatType);

  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);

  // Загружаем features
  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  // Показать toast
  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  // Обёртка для проверки доступа при выборе категории
  const handleSubOptionWithCheck = (subOption) => {
    const isProPlan = hasSubscription && (planType || '').toLowerCase() === 'pro';
    const forceAllowDocsEdit = subOption === 'docs_edit' && isProPlan;

    if (!canUseChat(subOption)) {
      if (!hasSubscription) {
        showToast("Этот чат доступен только по подписке");
      } else if ((planType || '').toLowerCase() === 'standard') {
        showToast("Этот чат доступен только на тарифе Pro. У вас тариф Стандарт");
      } else {
        showToast("Этот режим не включен в вашем доступе. Обновите права или обратитесь в поддержку");
        console.warn('[ChatCategories] Chat type is not available for current feature set', {
          requestedChatType: subOption,
          planType,
          availableChatTypes,
        });
      }
      return;
    }
    handleSubOptionChange(subOption);
  };

  /* ---------- media ---------- */
  useEffect(() => {
    const media = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(media.matches);
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  /* ---------- data ---------- */
  const allSubCategories = Object.values(categoryOptions).flatMap(
    (cat) => cat.subOptions
  );

  // desktop → infinite, mobile → обычный список
  const items = isMobile
    ? allSubCategories
    : [...allSubCategories, ...allSubCategories, ...allSubCategories];

  /* ---------- refs ---------- */
  const wrapperRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startScroll = useRef(0);
  const isAutoScrolling = useRef(false);

  /* ---------- desktop: старт в центре ---------- */
  useEffect(() => {
    if (isMobile) return;
    const el = wrapperRef.current;
    if (!el) return;
    el.scrollLeft = el.scrollWidth / 3;
  }, [isMobile]);

  /* ---------- desktop drag ---------- */
  const onMouseDown = (e) => {
    if (isMobile) return;
    isDragging.current = true;
    startX.current = e.pageX;
    startScroll.current = wrapperRef.current.scrollLeft;
  };

  const onMouseMove = (e) => {
    if (isMobile || !isDragging.current) return;
    const dx = e.pageX - startX.current;
    wrapperRef.current.scrollLeft = startScroll.current - dx;
  };

  const onMouseUp = () => {
    isDragging.current = false;
  };

  /* ---------- desktop infinite correction ---------- */
  const onScroll = () => {
    updateShadows();

    if (isMobile || isAutoScrolling.current) return;

    const el = wrapperRef.current;
    if (!el) return;

    const third = el.scrollWidth / 3;

    if (el.scrollLeft < third * 0.5) {
      el.scrollLeft += third;
    } else if (el.scrollLeft > third * 1.5) {
      el.scrollLeft -= third;
    }
  };


  /* ---------- smooth scroll helper ---------- */
  const smoothScroll = (container, target, duration = 320) => {
    const start = container.scrollLeft;
    const change = target - start;
    const startTime = performance.now();

    isAutoScrolling.current = true;

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      container.scrollLeft = start + change * easeInOut(progress);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        isAutoScrolling.current = false;
      }
    };

    requestAnimationFrame(animate);
  };

  const updateShadows = () => {
    const wrapper = wrapperRef.current;
    const container = containerRef.current;
    if (!wrapper || !container) return;

    const canScrollLeft = wrapper.scrollLeft > 5;
    const canScrollRight =
      wrapper.scrollLeft + wrapper.clientWidth <
      wrapper.scrollWidth - 5;

    container.classList.toggle("show-left", canScrollLeft);
    container.classList.toggle("show-right", canScrollRight);
  };

  /* ---------- CENTER ACTIVE (desktop + mobile) ---------- */
  useEffect(() => {
    const container = wrapperRef.current;
    if (!container) return;

    const buttons = container.querySelectorAll(".sub-item");
    if (!buttons.length) return;

    let target = null;

    if (isMobile) {
      target = container.querySelector(".sub-item.active");
    } else {
      const start = Math.floor(buttons.length / 3);
      const end = start * 2;

      for (let i = start; i < end; i++) {
        if (buttons[i].classList.contains("active")) {
          target = buttons[i];
          break;
        }
      }
    }

    if (!target) return;

    const targetCenter =
      target.offsetLeft + target.offsetWidth / 2;
    const containerCenter = container.clientWidth / 2;

    smoothScroll(container, targetCenter - containerCenter);

    setTimeout(updateShadows, 0);

  }, [selectedSubOption, isMobile]);

  useEffect(() => {
    updateShadows();
  }, [isMobile, items.length]);



  return (
    <div className="chat-categories" ref={containerRef}>
      {toast && (
        <div className="chat-categories-toast">
          {toast}
        </div>
      )}
      <div className="back_icon" onClick={handleClickToDialogs}>
        <FiSidebar className="sidebar_mover" />
      </div>

      <div className='newD_icon'>
                          <CiCirclePlus className='sidebar_mover'
                              data-tooltip-id="custom-tooltip"
                              data-tooltip-content="Добавить новый диалог"
                              data-tooltip-place="right"
                              onClick={() => {
                                handleStartNewDialog()
                              }}
                          />
      </div>


      <div className="chat-change">
        <div
          className="infinite-wrapper"
          ref={wrapperRef}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onScroll={onScroll}
        >
          <div className="infinite-track">
            {items.map((sub, index) => {
              const isProPlan = hasSubscription && (planType || '').toLowerCase() === 'pro';
              const forceAllowDocsEdit = sub.value === 'docs_edit' && isProPlan;
              const isAvailable = canUseChat(sub.value) || forceAllowDocsEdit;
              const showLock = loaded && !isAvailable;
              return (
                <button
                  key={`${sub.value}-${index}`}
                  className={`sub-item ${selectedSubOption === sub.value ? "active" : ""
                    } ${showLock ? "sub-item--locked" : ""}`}
                  onClick={() => handleSubOptionWithCheck(sub.value)}
                  title={showLock ? "Доступно на тарифе Pro" : ""}
                >
                  {sub.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatCategories;
